/**
 * @fileoverview TemplateAIChatSheet — AI editor sidebar for the precedent
 * library's TipTap editor.
 *
 * Composes the existing `ChatSheet` with a sticky **Review card** rendered
 * above the input. When the agent stages a proposal via `proposeRewrite`,
 * the card surfaces:
 *   - A short summary of what changed
 *   - A toggle between **Diff** (htmldiff-js inline diff) and **After**
 *     (the proposed HTML, rendered with `.prose-letter` so it looks like
 *     it would in the editor)
 *   - **Apply** / **Reject** buttons. Apply for `scope === "selection"`
 *     happens client-side via `editor.insertContent`; whole-document Apply
 *     calls the `applyProposal` Convex mutation which writes through the
 *     same path as the editor's autosave.
 *
 * The sheet talks to:
 *   - `api.precedents.chat.actions.sendChatMessage` (public action)
 *   - `api.precedents.chat.publicQueries.listChatMessages` (streamed)
 *   - `api.precedents.chat.publicQueries.getThreadForTemplate` (proposal blob)
 *   - `api.precedents.chat.mutations.applyProposal` / `clearProposal`
 *
 * Auth/gating mirrors the rest of the precedent library: only org
 * `owner`/`admin` users can open it, and the backend rejects everyone else.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import DOMPurify from "dompurify";
import {
  useQuery,
  useMutation as useReactQueryMutation,
} from "@tanstack/react-query";
import {
  convexQuery,
  useConvexAction,
  useConvexMutation,
} from "@convex-dev/react-query";
import {
  useStreamingUIMessages,
  useThreadMessages,
} from "@convex-dev/agent/react";
import { toast } from "sonner";
import { Check, X, GitCompare, Eye, Loader2 } from "lucide-react";
import type { Editor } from "@tiptap/react";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import {
  ChatSheet,
  type ChatMessageData,
  type ChatUserInfo,
} from "@/ui/composed/chat";
import { deriveToolInputStatus } from "@/ui/composed/chat/toolInputStatus";
import { Button } from "@/ui/primitives";
import { cn } from "@/utils/misc";
import { extractErrorMessage } from "@/utils/errors";
import { diffHtml } from "@/utils/precedents/htmlDiff";
import { prepareHtmlForEditor, buildFieldsLabelMap } from "./html-utils";
import type { TemplateEditorField } from "./template-editor";

interface TemplateAIChatSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  templateId: Id<"precedentTemplates">;
  /** TipTap editor instance — used to apply the proposal client-side. */
  editor: Editor | null;
  /** Fields the editor knows about (canonical + custom). Passed to
   *  `prepareHtmlForEditor` so `{{key}}` placeholders re-render as chips. */
  fields?: TemplateEditorField[];
  /** Currently selected HTML inside the editor (or `null` if no selection). */
  selectionHtml: string | null;
}

type ProposalScope = "whole" | "selection";

interface ProposalState {
  newHtml: string;
  summary: string;
  scope: ProposalScope;
  originalHtml: string;
  createdAt: number;
}

/**
 * Loose shape of the raw items returned by `useThreadMessages`. The
 * `@convex-dev/agent/react` types are a deeply-nested generic — narrowing
 * to the few fields we actually read keeps the rest of this file
 * `no-explicit-any`-clean.
 */
interface RawAgentMessage {
  _id?: string;
  _creationTime: number;
  key?: string;
  streaming?: boolean;
  message?: {
    role?: string;
    content?:
      | string
      | Array<{
          type?: string;
          text?: string;
          toolName?: string;
        }>;
  };
}

export function TemplateAIChatSheet({
  open,
  onOpenChange,
  templateId,
  editor,
  fields,
  selectionHtml,
}: TemplateAIChatSheetProps) {
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);
  const [waitingForResponse, setWaitingForResponse] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [diffMode, setDiffMode] = useState<"diff" | "after">("diff");
  const [applying, setApplying] = useState(false);

  const fieldsMap = useMemo(() => buildFieldsLabelMap(fields), [fields]);

  // Resolve the current user for avatar / first name display in the chat list.
  const { data: currentUser } = useQuery(
    convexQuery(api.users.queries.getCurrentUser, {}),
  );
  const userInfo = useMemo<ChatUserInfo | undefined>(() => {
    if (!currentUser) return undefined;
    const u = currentUser as Record<string, unknown>;
    return {
      avatarUrl: u.avatarUrl as string | undefined,
      firstName: u.firstName as string | undefined,
      lastName: u.lastName as string | undefined,
      email: u.email as string | undefined,
    };
  }, [currentUser]);

  // Reactive query: thread id + pending proposal blob for this template/user pair.
  const { data: threadInfo } = useQuery(
    convexQuery(api.precedents.chat.publicQueries.getThreadForTemplate, {
      templateId,
    }),
  );
  const activeThreadId = threadInfo?.threadId ?? null;
  const fieldChange = threadInfo?.lastFieldChange ?? null;
  const proposal: ProposalState | null = useMemo(() => {
    const p = threadInfo?.pendingProposal;
    if (!p) return null;
    return {
      newHtml: p.newHtml,
      summary: p.summary,
      scope: p.scope,
      originalHtml: p.originalHtml,
      createdAt: p.createdAt,
    };
  }, [threadInfo?.pendingProposal]);

  // Subscribe to streaming messages for the active thread.
  const threadMessages = useThreadMessages(
    api.precedents.chat.publicQueries.listChatMessages,
    activeThreadId ? { threadId: activeThreadId } : "skip",
    { initialNumItems: 30, stream: true },
  );

  // Streaming UIMessages expose partial tool-call input (state
  // "input-streaming") that the MessageDoc mapping below drops — the only
  // signal that the model is mid-way through writing the rewrite into the
  // proposeRewrite tool argument. Powers the live "Drafting template
  // rewrite — Nk characters" pill during what used to be a silent wait.
  const streamingUIMessages = useStreamingUIMessages(
    api.precedents.chat.publicQueries.listChatMessages,
    activeThreadId
      ? {
          threadId: activeThreadId,
          // The hook only reads the `streams` half of the response — an
          // empty page keeps the shared query shape happy (same trick
          // useThreadMessages uses internally for its stream leg).
          paginationOpts: { cursor: null, numItems: 0 },
        }
      : "skip",
  );
  const activityStatus = useMemo(
    () => deriveToolInputStatus(streamingUIMessages),
    [streamingUIMessages],
  );

  // Map raw thread messages → ChatMessageData. Mirrors SummaryChatSheet so
  // the visual treatment is identical.
  const realMessages: ChatMessageData[] = useMemo(() => {
    const raw = (threadMessages?.results ?? []) as RawAgentMessage[];
    return raw
      .filter((m) => {
        const role = m.message?.role;
        if (role !== "user" && role !== "assistant") return false;
        const content = m.message?.content;
        if (!content) return false;
        if (typeof content === "string") return content.trim().length > 0;
        return content.some(
          (part) => part.type === "text" && part.text?.trim(),
        );
      })
      .map((m) => {
        const content = m.message?.content ?? "";
        const text =
          typeof content === "string"
            ? content
            : content
                .map((p) =>
                  p.type === "text" && typeof p.text === "string" ? p.text : "",
                )
                .join("");
        return {
          id: m.key ?? m._id ?? String(m._creationTime),
          role: m.message?.role as "user" | "assistant",
          content: text,
          createdAt: m._creationTime,
          isStreaming: m.streaming === true,
        };
      });
  }, [threadMessages?.results]);

  // Clear optimistic message once the real one arrives.
  useEffect(() => {
    if (
      pendingMessage &&
      realMessages.some(
        (m) => m.role === "user" && m.content === pendingMessage,
      )
    ) {
      setPendingMessage(null);
    }
  }, [realMessages, pendingMessage]);

  const messages: ChatMessageData[] = useMemo(() => {
    if (!pendingMessage) return realMessages;
    if (
      realMessages.some(
        (m) => m.role === "user" && m.content === pendingMessage,
      )
    ) {
      return realMessages;
    }
    return [
      ...realMessages,
      {
        id: "optimistic-pending",
        role: "user" as const,
        content: pendingMessage,
        createdAt: Date.now(),
      },
    ];
  }, [realMessages, pendingMessage]);

  // Derive thinking state + active tool calls so the chat shows progress
  // through tool-call cycles (getCurrentTemplate → proposeRewrite).
  const { isAgentThinking, activeToolCalls } = useMemo(() => {
    const raw = (threadMessages?.results ?? []) as RawAgentMessage[];
    if (raw.length === 0) {
      return {
        isAgentThinking: waitingForResponse,
        activeToolCalls: [] as string[],
      };
    }

    const tools: string[] = [];
    for (let i = raw.length - 1; i >= 0; i--) {
      const m = raw[i];
      if (m.message?.role === "user") break;
      const content = m.message?.content;
      if (m.message?.role === "assistant" && Array.isArray(content)) {
        for (const part of content) {
          if (part.type === "tool-call" && part.toolName) {
            tools.push(part.toolName);
          }
        }
      }
    }

    const lastRaw = raw[raw.length - 1];
    const lastRole = lastRaw.message?.role;

    if (lastRaw.streaming && lastRole === "assistant") {
      return { isAgentThinking: false, activeToolCalls: [] as string[] };
    }

    const lastContent = lastRaw.message?.content;
    const hasToolCallParts =
      lastRole === "assistant" &&
      Array.isArray(lastContent) &&
      lastContent.some((p) => p.type === "tool-call");

    const isWorking =
      waitingForResponse || lastRole === "tool" || hasToolCallParts;

    return {
      isAgentThinking: isWorking,
      activeToolCalls: isWorking ? tools : [],
    };
  }, [threadMessages?.results, waitingForResponse]);

  useEffect(() => {
    if (waitingForResponse && !isAgentThinking) {
      setWaitingForResponse(false);
    }
  }, [waitingForResponse, isAgentThinking]);

  // Mutations / actions
  const { mutateAsync: sendAction } = useReactQueryMutation({
    mutationFn: useConvexAction(api.precedents.chat.actions.sendChatMessage),
  });
  const { mutateAsync: applyProposalMutation } = useReactQueryMutation({
    mutationFn: useConvexMutation(api.precedents.chat.mutations.applyProposal),
  });
  const { mutateAsync: clearProposalMutation } = useReactQueryMutation({
    mutationFn: useConvexMutation(api.precedents.chat.mutations.clearProposal),
  });
  const { mutateAsync: clearFieldChangeMutation } = useReactQueryMutation({
    mutationFn: useConvexMutation(
      api.precedents.chat.mutations.clearFieldChangeNote,
    ),
  });

  const handleSend = useCallback(
    async (message: string) => {
      setPendingMessage(message);
      setWaitingForResponse(true);
      setIsSending(true);
      try {
        await sendAction({
          templateId,
          message,
          selectionHtml: selectionHtml ?? undefined,
          threadId: activeThreadId ?? undefined,
        });
      } catch (err) {
        console.error("[TemplateAIChatSheet] send failed", err);
        toast.error(extractErrorMessage(err, "Failed to send message"));
        setPendingMessage(null);
        setWaitingForResponse(false);
      } finally {
        setTimeout(() => setIsSending(false), 400);
      }
    },
    [sendAction, templateId, selectionHtml, activeThreadId],
  );

  const handleApply = useCallback(async () => {
    if (!proposal || !editor || applying) return;
    setApplying(true);
    try {
      if (proposal.scope === "whole") {
        await applyProposalMutation({ templateId });
        // Sync the local editor to the new HTML so the user sees the
        // change immediately without waiting for a parent re-render.
        editor
          .chain()
          .focus()
          .setContent(prepareHtmlForEditor(proposal.newHtml, fieldsMap))
          .run();
        toast.success("Applied AI rewrite to the template.");
      } else {
        // Selection scope: replace the user's selection client-side. The
        // editor's onBlur autosave persists the change. Then clear the
        // proposal server-side.
        editor
          .chain()
          .focus()
          .deleteSelection()
          .insertContent(prepareHtmlForEditor(proposal.newHtml, fieldsMap))
          .run();
        await clearProposalMutation({ templateId });
        toast.success("Inserted AI rewrite at your selection.");
      }
    } catch (err) {
      console.error("[TemplateAIChatSheet] apply failed", err);
      toast.error(extractErrorMessage(err, "Failed to apply rewrite"));
    } finally {
      setApplying(false);
    }
  }, [
    proposal,
    editor,
    applying,
    applyProposalMutation,
    clearProposalMutation,
    templateId,
    fieldsMap,
  ]);

  const handleReject = useCallback(async () => {
    if (!proposal) return;
    try {
      await clearProposalMutation({ templateId });
      toast.info("Discarded AI rewrite.");
    } catch (err) {
      console.error("[TemplateAIChatSheet] reject failed", err);
      toast.error(extractErrorMessage(err, "Failed to discard rewrite"));
    }
  }, [proposal, clearProposalMutation, templateId]);

  const handleDismissFieldChange = useCallback(async () => {
    try {
      await clearFieldChangeMutation({ templateId });
    } catch (err) {
      console.error("[TemplateAIChatSheet] dismiss field note failed", err);
    }
  }, [clearFieldChangeMutation, templateId]);

  const description = selectionHtml
    ? "Working on your selected text. Ask Morgan to rewrite, restructure, or improve it."
    : "Ask Morgan to rewrite, restructure, or improve this template.";

  return (
    <ChatSheet
      open={open}
      onOpenChange={onOpenChange}
      title="Ask AI"
      description={description}
      messages={messages}
      onSend={handleSend}
      userInfo={userInfo}
      isSending={isSending}
      isThinking={isAgentThinking}
      activeToolCalls={activeToolCalls}
      activityStatus={activityStatus}
      emptyMessage={
        selectionHtml
          ? "You have text selected. Ask for a rewrite, e.g. 'make this more formal' or 'shorten this paragraph'."
          : "Ask for a rewrite, e.g. 'rewrite the introduction to sound more formal' or 'add a section explaining what happens next'."
      }
      inputPlaceholder={
        selectionHtml
          ? "Ask about the selected text…"
          : "Ask Morgan to edit this template…"
      }
      inputBanner={
        proposal ? (
          <ReviewCard
            proposal={proposal}
            diffMode={diffMode}
            onDiffModeChange={setDiffMode}
            onApply={handleApply}
            onReject={handleReject}
            applying={applying}
            hasEditor={editor !== null}
          />
        ) : fieldChange ? (
          <FieldChangeCard
            applied={fieldChange.applied}
            errors={fieldChange.errors}
            onDismiss={handleDismissFieldChange}
          />
        ) : null
      }
    />
  );
}

// ---------------------------------------------------------------------------
// Review Card
// ---------------------------------------------------------------------------

function ReviewCard({
  proposal,
  diffMode,
  onDiffModeChange,
  onApply,
  onReject,
  applying,
  hasEditor,
}: {
  proposal: ProposalState;
  diffMode: "diff" | "after";
  onDiffModeChange: (next: "diff" | "after") => void;
  onApply: () => void;
  onReject: () => void;
  applying: boolean;
  hasEditor: boolean;
}) {
  // `newHtml` is agent (LLM) output that has not yet round-tripped the TipTap
  // schema, so both preview modes sanitise before injecting. Editor HTML is
  // designed to survive a DOMPurify round-trip (see horizontal-rule-extension)
  // and field-chip `data-*` attributes pass DOMPurify's defaults.
  const diffMarkup = useMemo(() => {
    if (diffMode !== "diff") return "";
    return DOMPurify.sanitize(
      diffHtml(proposal.originalHtml, proposal.newHtml),
    );
  }, [diffMode, proposal.originalHtml, proposal.newHtml]);
  const safeNewHtml = useMemo(
    () => DOMPurify.sanitize(proposal.newHtml),
    [proposal.newHtml],
  );

  return (
    <div className="border-t border-border bg-background/95 px-4 pb-3 pt-3 backdrop-blur-sm">
      <div className="rounded-lg border border-primary/15 bg-primary/[0.03] shadow-sm">
        <div className="flex items-start gap-2 px-3 pt-2.5">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-primary/60">
                {proposal.scope === "selection"
                  ? "Selection rewrite"
                  : "Document rewrite"}
              </span>
              <span className="text-[10px] text-muted-foreground/60">
                · staged just now
              </span>
            </div>
            <p className="mt-1 text-sm leading-snug text-foreground/90">
              {proposal.summary}
            </p>
          </div>
          <div className="inline-flex shrink-0 overflow-hidden rounded-md border border-border bg-background">
            <button
              type="button"
              onClick={() => onDiffModeChange("diff")}
              className={cn(
                "inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium transition",
                diffMode === "diff"
                  ? "bg-primary/10 text-primary"
                  : "text-muted-foreground hover:bg-muted/50",
              )}
              title="Show inline diff"
            >
              <GitCompare className="h-3 w-3" />
              Diff
            </button>
            <button
              type="button"
              onClick={() => onDiffModeChange("after")}
              className={cn(
                "inline-flex items-center gap-1 border-l border-border px-2 py-1 text-[11px] font-medium transition",
                diffMode === "after"
                  ? "bg-primary/10 text-primary"
                  : "text-muted-foreground hover:bg-muted/50",
              )}
              title="Show proposed result"
            >
              <Eye className="h-3 w-3" />
              After
            </button>
          </div>
        </div>

        <div className="mx-3 mt-2 max-h-56 overflow-auto rounded-md border border-border bg-background p-3">
          {diffMode === "diff" ? (
            <div
              className="html-diff prose-letter text-sm"
              // eslint-disable-next-line @eslint-react/dom-no-dangerously-set-innerhtml -- diffMarkup is DOMPurify.sanitize'd in the memo above (LLM-proposed HTML)
              dangerouslySetInnerHTML={{ __html: diffMarkup }}
            />
          ) : (
            <div
              className="prose-letter text-sm"
              // eslint-disable-next-line @eslint-react/dom-no-dangerously-set-innerhtml -- safeNewHtml is DOMPurify.sanitize'd in the memo above (LLM-proposed HTML)
              dangerouslySetInnerHTML={{ __html: safeNewHtml }}
            />
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-3 pb-2.5 pt-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={onReject}
            disabled={applying}
            className="h-7 gap-1 px-2.5 text-xs"
          >
            <X className="h-3 w-3" />
            Reject
          </Button>
          <Button
            size="sm"
            onClick={onApply}
            disabled={applying || !hasEditor}
            className="h-7 gap-1 px-3 text-xs"
          >
            {applying ? (
              <>
                <Loader2 className="h-3 w-3 animate-spin" />
                Applying…
              </>
            ) : (
              <>
                <Check className="h-3 w-3" />
                Apply
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Field-change confirmation
// ---------------------------------------------------------------------------

/**
 * Confirmation for field changes the agent applied (MOR-920).
 *
 * Field edits are metadata-level and reversible, so they save immediately
 * instead of going through the Apply/Reject diff. Without this note the change
 * would be invisible in the chat, which reads as "nothing happened".
 */
function FieldChangeCard({
  applied,
  errors,
  onDismiss,
}: {
  applied: string[];
  errors: string[];
  onDismiss: () => void;
}) {
  return (
    <div className="rounded-lg border border-emerald-300/70 bg-emerald-50/80 p-3 dark:border-emerald-900/50 dark:bg-emerald-950/25">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-xs font-semibold text-emerald-900 dark:text-emerald-200">
            {applied.length > 0
              ? `Template fields updated (${applied.length})`
              : "No field changes applied"}
          </p>
          <p className="mt-0.5 text-[11px] text-emerald-900/70 dark:text-emerald-200/70">
            Saved automatically — no Apply needed.
          </p>
          {applied.length > 0 && (
            <ul className="mt-1.5 space-y-0.5">
              {applied.map((line) => (
                <li
                  key={line}
                  className="font-mono text-[11px] text-emerald-900/80 dark:text-emerald-200/80"
                >
                  {line}
                </li>
              ))}
            </ul>
          )}
          {errors.length > 0 && (
            <ul className="mt-1.5 space-y-0.5">
              {errors.map((line) => (
                <li
                  key={line}
                  className="text-[11px] text-amber-700 dark:text-amber-300"
                >
                  {line}
                </li>
              ))}
            </ul>
          )}
        </div>
        <Button
          size="sm"
          variant="ghost"
          className="h-7 shrink-0 text-xs"
          onClick={onDismiss}
        >
          Dismiss
        </Button>
      </div>
    </div>
  );
}
